import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { normalizeCipherState, useGameStore } from '../../store/gameStore';
import {
  appendRevealDiff,
  applyActionResult,
  nestActionEntityState,
} from '../../utils/actionResult';
import { gameAPI } from '../../services/gameAPI';
import {
  Lock,
  Key,
  Check,
  AlertTriangle,
  Loader2,
  Award,
  BookOpen,
  HelpCircle,
  Search,
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

// Themed cipher UI components
import { SafeDialUI, BriefcaseUI, TerminalUI } from './cipher';

// Theme to UI component mapping
const THEMED_UI_COMPONENTS = {
  noir: SafeDialUI,
  '90s_nyc': TerminalUI,
  miami_vice: BriefcaseUI,
};

// Cipher type display names and descriptions
const CIPHER_INFO = {
  atbash: {
    name: 'Atbash Cipher',
    description: 'A simple substitution cipher where the alphabet is reversed (A=Z, B=Y, etc.)',
    hint: 'The first letter becomes the last, and vice versa.',
  },
  caesar_small: {
    name: 'Caesar Cipher',
    description: 'Each letter is shifted by a small number (1-3) in the alphabet.',
    hint: 'Look for small numbers mentioned in your clues.',
  },
  caesar_full: {
    name: 'Caesar Cipher',
    description: 'Each letter is shifted by a number in the alphabet.',
    hint: 'Find the shift value hidden in the evidence.',
  },
  keyword: {
    name: 'Keyword Cipher',
    description: 'A substitution cipher using a keyword to scramble the alphabet.',
    hint: 'The keyword creates a new alphabet - find it in the clues.',
  },
  vigenere: {
    name: 'Vigenere Cipher',
    description: 'A polyalphabetic cipher using a repeating keyword.',
    hint: 'The keyword repeats - each letter shifts by a different amount.',
  },
};

// Theme-specific styling
const THEME_CONFIG = {
  noir: {
    accent: '#DC143C',
    bgGradient: 'from-slate-950 via-slate-900 to-slate-950',
    cipherBg: 'bg-slate-800 border-slate-700',
    fontFamily: 'font-serif',
    title: 'Coded Message',
    subtitle: 'Found among the victim\'s belongings',
  },
  '90s_nyc': {
    accent: '#FF6B35',
    bgGradient: 'from-zinc-950 via-zinc-900 to-zinc-950',
    cipherBg: 'bg-zinc-800 border-zinc-700',
    fontFamily: 'font-mono',
    title: 'Encrypted Note',
    subtitle: 'Tagged evidence from the scene',
  },
  miami_vice: {
    accent: '#FF1493',
    bgGradient: 'from-purple-950 via-slate-900 to-cyan-950',
    cipherBg: 'bg-purple-900/50 border-purple-700',
    fontFamily: 'font-sans',
    title: 'Coded Transmission',
    subtitle: 'Intercepted communication',
  },
};

/**
 * Normalize the OPTIONAL cipher reward payload.
 *
 * The backend may omit `reward` entirely (no reward configured, and every
 * legacy case that carries a bare `reward_type` with no payload). Anything
 * without a concrete `clue_id` resolves to null so no empty box is rendered.
 */
const normalizeCipherReward = (reward) => {
  if (!reward || typeof reward !== 'object') return null;

  const clueId = reward.clue_id ?? reward.clueId;
  if (!clueId) return null;

  return {
    clueId,
    name: reward.clue_name ?? reward.clueName ?? '',
    description: reward.clue_description ?? reward.clueDescription ?? '',
    alreadyDiscovered: Boolean(reward.already_discovered ?? reward.alreadyDiscovered),
  };
};

/**
 * Evidence granted by solving the cipher. Visual idiom matches the
 * "Evidence Discovered" list in ExamineModal.
 */
const CipherRewardPanel = ({ reward, accent }) => {
  if (!reward) return null;

  const { alreadyDiscovered } = reward;

  return (
    <div
      className={`mt-4 p-3 rounded-lg border ${
        alreadyDiscovered
          ? 'bg-slate-800/60 border-slate-600'
          : 'bg-green-900/30 border-green-700'
      }`}
    >
      <h4
        className={`text-sm font-semibold mb-2 flex items-center gap-2 ${
          alreadyDiscovered ? 'text-slate-300' : 'text-green-400'
        }`}
      >
        <Search className="w-4 h-4" style={{ color: accent }} />
        {alreadyDiscovered ? 'Evidence Confirmed:' : 'Evidence Discovered:'}
      </h4>
      <div className="flex items-start gap-2">
        <Check
          className={`w-3 h-3 mt-1 flex-shrink-0 ${
            alreadyDiscovered ? 'text-slate-400' : 'text-green-400'
          }`}
        />
        <div className="min-w-0">
          <p className={`text-sm ${alreadyDiscovered ? 'text-slate-200' : 'text-green-300'}`}>
            {reward.name || 'New evidence'}
          </p>
          {reward.description && (
            <p className="text-xs text-slate-400 mt-1">{reward.description}</p>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-2">
        {alreadyDiscovered
          ? 'The message confirms evidence already in your case file.'
          : 'Added to your case file.'}
      </p>
    </div>
  );
};

export const CipherModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);

  const sessionId = useGameStore((state) => state.sessionId);
  const selectedTheme = useGameStore((state) => state.selectedTheme);
  const cipherState = useGameStore((state) => state.cipherState);
  const cipherPuzzle = useGameStore((state) => state.cipherPuzzle);
  const updateCipherState = useGameStore((state) => state.updateCipherState);
  const incrementCipherAttempts = useGameStore((state) => state.incrementCipherAttempts);
  const solveCipher = useGameStore((state) => state.solveCipher);
  const updateGameState = useGameStore((state) => state.updateGameState);
  const addToAnimationQueue = useGameStore((state) => state.addToAnimationQueue);

  const theme = THEME_CONFIG[selectedTheme] || THEME_CONFIG.noir;
  const cipherInfo = CIPHER_INFO[cipherPuzzle?.cipher_type] || CIPHER_INFO.caesar_small;
  const rawHintsTotal = cipherPuzzle?.hints_total ?? cipherPuzzle?.hintsTotal;
  const hintsTotal = Number.isFinite(Number(rawHintsTotal))
    ? Number(rawHintsTotal)
    : (cipherPuzzle?.hints?.length || 0);

  // Get the themed UI component
  const ThemedUI = THEMED_UI_COMPONENTS[selectedTheme] || SafeDialUI;

  // Fetch cipher state from backend when modal opens
  useEffect(() => {
    if (!open || !sessionId) return;

    const fetchCipherState = async () => {
      setLoadingState(true);
      try {
        const data = await gameAPI.getCipherState(sessionId);
        // Update local state with backend data
        const backendCipherState = data.cipher_state ?? data.cipherState;
        if (backendCipherState) {
          updateCipherState(normalizeCipherState(backendCipherState, useGameStore.getState().cipherState));
        }
        const backendCipherPuzzle = data.cipher_puzzle ?? data.cipherPuzzle;
        if (backendCipherPuzzle) {
          // Preserve any reward we already know about (either persisted by the
          // backend or recorded locally when the player solved the cipher) so
          // reopening a solved cipher still shows the evidence it granted.
          const persistedReward =
            data.reward
            ?? backendCipherPuzzle.reward
            ?? data.cipher_state?.reward
            ?? data.cipherState?.reward
            ?? useGameStore.getState().cipherPuzzle?.reward;
          updateGameState({
            cipherPuzzle: {
              ...backendCipherPuzzle,
              ...(persistedReward ? { reward: persistedReward } : {}),
            },
          });
        }
      } catch (err) {
        console.error('Failed to fetch cipher state:', err);
        setError('Failed to load cipher puzzle');
      } finally {
        setLoadingState(false);
      }
    };

    fetchCipherState();
  }, [open, sessionId, updateCipherState, updateGameState]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setResult(null);
      setError(null);
    }
  }, [open]);

  // Get discovered hints
  const discoveredHints = useMemo(() => {
    if (!cipherPuzzle?.hints || !cipherState?.hintsFound) return [];
    return cipherPuzzle.hints.filter(h => cipherState.hintsFound.includes(h.id));
  }, [cipherPuzzle, cipherState?.hintsFound]);

  // Check if showing tutorial (easy mode, first time)
  const shouldShowTutorial = cipherPuzzle?.show_tutorial && cipherState?.attempts === 0;

  // Reward granted by the cipher, sourced from persisted puzzle state so it
  // survives closing and reopening the modal. Null when the case has none.
  const persistedReward = normalizeCipherReward(cipherPuzzle?.reward);

  // Handle solution submission (called from themed UI)
  const handleSubmit = async (solution) => {
    if (!solution || !solution.trim()) {
      toast({ title: 'Enter a solution', description: 'Type your decoded message' });
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await gameAPI.attemptCipherSolution(sessionId, solution.trim());

      if (response.correct) {
        // Success! `reward` is optional — absent for cases with no cipher reward.
        const reward = normalizeCipherReward(response.reward);

        setResult({
          correct: true,
          message: response.message || 'Correct! The cipher has been decoded.',
          plaintext: response.plaintext,
          reward,
        });
        if (response.cipher_state || response.cipherState) {
          updateCipherState(normalizeCipherState(
            response.cipher_state ?? response.cipherState,
            useGameStore.getState().cipherState
          ));
        } else {
          solveCipher();
        }

        // Update cipher puzzle state with plaintext (and the reward, so the
        // already-solved view can still show it when the modal is reopened)
        updateGameState({
          cipherPuzzle: {
            ...cipherPuzzle,
            plaintext: response.plaintext,
            ...(response.reward ? { reward: response.reward } : {}),
          },
        });

        // Merge any revealed clues/discovery state. Union-merge via the shared
        // action-result helpers so item arrays are never clobbered, badges get
        // queued, and a missing revealed object flags a session refresh.
        const cipherUpdatedState = response.updatedState || response.updated_state;
        if (cipherUpdatedState) {
          const previousGameState = useGameStore.getState();
          const actionResult = applyActionResult(previousGameState, response);
          const revealDiff = actionResult.revealDiff;
          const entityState = nestActionEntityState(actionResult.nextState);

          updateGameState({
            discovered: entityState.discovered,
            suspects: entityState.suspects,
            clues: entityState.clues,
            locations: entityState.locations,
            documents: entityState.documents,
            newlyDiscovered: actionResult.needsSessionRefresh
              ? previousGameState.newlyDiscovered
              : appendRevealDiff(previousGameState.newlyDiscovered, revealDiff),
            needsSessionRefresh: actionResult.needsSessionRefresh,
            // applyActionResult has no hintedClues handling, so thread it the same
            // way ActionBar.jsx does (~L357): the granted clue is dropped from
            // `hinted_clues` server-side and must leave the yellow Hints panel too.
            hintedClues: cipherUpdatedState.hintedClues || previousGameState.hintedClues,
            // The reward clue can sit at a location the player has already
            // examined, which moves that location's "N of M" numerator. The
            // records are re-derived server-side on every payload, so take
            // them whenever the response carries them.
            examinations: cipherUpdatedState.examinations || previousGameState.examinations,
          });
        }

        // Surface the granted clue as a discovery toast (only for a genuinely
        // new find — a clue the player already had is not a new discovery)
        if (reward && !reward.alreadyDiscovered) {
          addToAnimationQueue('clues', reward.clueId);
        }

        toast({
          title: 'Cipher Decoded!',
          description: response.message || 'You\'ve uncovered a hidden message.',
        });
      } else {
        // Wrong answer
        incrementCipherAttempts();
        setResult({
          correct: false,
          message: response.message || 'That\'s not quite right.',
          escalation_hint: response.escalation_hint,
        });

        // Show escalation hint if provided
        if (response.escalation_hint) {
          toast({
            title: 'Not quite...',
            description: response.escalation_hint,
          });
        }
      }
    } catch (err) {
      console.error('Cipher attempt failed:', err);
      setError(err.response?.data?.detail || 'Failed to submit solution');
      toast({
        title: 'Error',
        description: 'Failed to check your solution',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // If cipher is already solved
  if (cipherState?.solved) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className={`w-full max-w-xl bg-gradient-to-b ${theme.bgGradient} border-2`}
          style={{ borderColor: theme.accent }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Award className="w-5 h-5" style={{ color: theme.accent }} />
              Cipher Decoded
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${theme.cipherBg} border`}>
              <p className="text-sm text-slate-400 mb-2">Decoded Message:</p>
              <p className={`text-lg text-white ${theme.fontFamily}`}>
                {cipherPuzzle?.plaintext || 'THE TRUTH HAS BEEN REVEALED'}
              </p>
            </div>

            <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Check className="w-5 h-5 text-green-500" />
                <span className="font-semibold text-green-400">Cipher Solved</span>
              </div>
              <p className="text-sm text-green-300">
                You've successfully decoded the hidden message and gained valuable insight into the case.
              </p>
            </div>

            <CipherRewardPanel reward={persistedReward} accent={theme.accent} />

            <Button
              onClick={onClose}
              className="w-full"
              style={{ backgroundColor: theme.accent }}
            >
              Continue Investigation
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Loading state
  if (loadingState) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className={`w-full max-w-xl bg-gradient-to-b ${theme.bgGradient} border-2`}
          style={{ borderColor: theme.accent }}
        >
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.accent }} />
            <p className="text-slate-400">Loading cipher puzzle...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // No cipher puzzle available
  if (!cipherPuzzle) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className={`w-full max-w-xl bg-gradient-to-b ${theme.bgGradient} border-2`}
          style={{ borderColor: theme.accent }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Lock className="w-5 h-5" style={{ color: theme.accent }} />
              {theme.title}
            </DialogTitle>
          </DialogHeader>

          <div className="py-8 text-center">
            <Lock className="w-12 h-12 mx-auto mb-4 text-slate-500" />
            <p className="text-slate-300 mb-2">No cipher puzzle found</p>
            <p className="text-sm text-slate-500">
              Continue investigating to discover coded messages.
            </p>
          </div>

          <Button
            onClick={onClose}
            variant="outline"
            className="w-full border-slate-600 text-white hover:bg-slate-800"
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={`w-full h-[100dvh] sm:max-w-2xl sm:h-auto sm:max-h-[90vh] overflow-y-auto bg-gradient-to-b ${theme.bgGradient} border-2`}
        style={{ borderColor: theme.accent }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2 pr-10">
            <Lock className="w-5 h-5" style={{ color: theme.accent }} />
            {theme.title}
          </DialogTitle>
          <p className="text-sm text-slate-400">{theme.subtitle}</p>
        </DialogHeader>

        {/* Tutorial/Help section */}
        {(shouldShowTutorial || showTutorial) && (
          <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-300 mb-1">{cipherInfo.name}</h4>
                <p className="text-sm text-blue-200 mb-2">{cipherInfo.description}</p>
                <p className="text-xs text-blue-300 italic">{cipherInfo.hint}</p>
              </div>
            </div>
            {showTutorial && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTutorial(false)}
                className="mt-2 text-blue-400 hover:text-blue-300"
              >
                Hide Tutorial
              </Button>
            )}
          </div>
        )}

        {/* Tutorial toggle for non-easy mode */}
        {!showTutorial && !shouldShowTutorial && (
          <div className="flex justify-end mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowTutorial(true)}
              className="text-slate-400 hover:text-slate-300 text-xs"
            >
              <HelpCircle className="w-3 h-3 mr-1" />
              How to decode
            </Button>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <p className="text-red-300">{error}</p>
            </div>
          </div>
        )}

        {/* Themed UI Component */}
        <ThemedUI
          ciphertext={cipherPuzzle.ciphertext || 'LOADING...'}
          hints={discoveredHints}
          onSubmit={handleSubmit}
          loading={loading}
          result={result}
          attempts={cipherState?.attempts || 0}
          solved={cipherState?.solved}
          cipherType={cipherPuzzle?.cipher_type}
        />

        {/* Evidence granted by solving the cipher (optional — omitted by cases
            with no cipher reward, which is every legacy case) */}
        {result?.correct && (
          <CipherRewardPanel reward={result.reward} accent={theme.accent} />
        )}

        {/* Continue button */}
        <div className="mt-4">
          <Button
            onClick={onClose}
            variant="outline"
            className="w-full border-slate-600 text-white hover:bg-slate-800"
          >
            Continue Investigating
          </Button>
        </div>

        {/* Hint about discovering more clues */}
        {discoveredHints.length < hintsTotal && (
          <p className="text-xs text-center text-slate-500 mt-2">
            More clues may be hidden in documents, evidence, or suspect conversations.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CipherModal;
